/**
 * Sheet — Side panel overlay for detail views, filters, or settings.
 *
 * Sheet Component Guidelines
 * - Use Sheet for side panels that overlay the page while keeping the main content
 *   partially visible. Prefer Modal when the user needs full focus on the task.
 *   Prefer Drawer when the panel should be draggable/swipeable (mobile patterns).
 * - Default `side` is `"right"`. Use `side="left"` for navigation panels or filters
 *   that conventionally live on the left.
 * - Use `showCloseButton={false}` only when the sheet has its own explicit close
 *   mechanism (e.g. a done/cancel button in the footer).
 * - Sheet is composable-only — assemble with `SheetContent`, `SheetHeader`,
 *   `SheetTitle`, `SheetDescription`, and `SheetFooter` as needed.
 * @see https://base-ui.com/react/components/dialog Base UI Dialog documentation
 * @see https://ui.shadcn.com/docs/components/sheet shadcn/ui Sheet documentation
 */
import * as React from "react";

import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { Button } from "@replohq/lattice/components/button/Button";
import {
  PortalThemeScope,
  usePortalContainer,
} from "@replohq/lattice/components/portal-theme/PortalTheme";
import { cn } from "@replohq/lattice/utils/cn";
import { XIcon } from "lucide-react";

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ children, ...props }: SheetPrimitive.Portal.Props) {
  const container = usePortalContainer();
  return (
    <SheetPrimitive.Portal
      data-slot="sheet-portal"
      container={container}
      {...props}
    >
      <PortalThemeScope>{children}</PortalThemeScope>
    </SheetPrimitive.Portal>
  );
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-default/10 duration-micro ease-out-quint data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 z-50 motion-reduce:animate-none motion-reduce:transition-none",
        // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        className as string,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: "top" | "right" | "bottom" | "left";
  showCloseButton?: boolean;
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          "bg-sheet text-default data-open:animate-in data-closed:animate-out data-[side=right]:data-closed:slide-out-to-right-10 data-[side=right]:data-open:slide-in-from-right-10 data-[side=left]:data-closed:slide-out-to-left-10 data-[side=left]:data-open:slide-in-from-left-10 data-[side=top]:data-closed:slide-out-to-top-10 data-[side=top]:data-open:slide-in-from-top-10 data-closed:fade-out-0 data-open:fade-in-0 data-[side=bottom]:data-closed:slide-out-to-bottom-10 data-[side=bottom]:data-open:slide-in-from-bottom-10 fixed z-50 flex flex-col gap-4 bg-clip-padding text-sm shadow-lg transition duration-standard data-closed:duration-fast ease-out-quint data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t-0.5 data-[side=bottom]:border-border data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r-0.5 data-[side=left]:border-border data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l-0.5 data-[side=right]:border-border data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b-0.5 data-[side=top]:border-border data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm motion-reduce:animate-none motion-reduce:transition-none",
          // Legacy cast - may or may not be necessary
          // eslint-disable-next-line replo/no-as-cast
          className as string,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            render={
              <Button
                variant="tertiary"
                className="absolute top-3 right-3"
                icon
                size="base"
              >
                <XIcon />
                <span className="sr-only">Close</span>
              </Button>
            }
          />
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("gap-0.5 p-4 flex flex-col", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("gap-2 p-4 mt-auto flex flex-col", className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        "text-default text-base font-medium",
        // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        className as string,
      )}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      className={cn("text-muted text-sm", className as string)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
